import { API } from 'aws-amplify';
import React from 'react';
import { AppContext } from '../contexts/app-context';
import { AuthContext } from '../contexts/auth-context';
import { useAllianceIdentity } from '../hooks/use-alliance-identity';
import { useSessionActions } from '../hooks/use-session-actions';
import { AgentSelect } from '../types';
import { captureError } from '../utils/capture-error';
import {
  ImpersonationSearch,
  SearchAgent,
} from './alliance/impersonation-search';

/** "First Last" for a /agent/search row, falling back to agentname */
const agentName = (params: { agent: AgentSelect }) =>
  [params.agent.fname, params.agent.lname].filter(Boolean).join(' ') ||
  params.agent.agentname ||
  params.agent.agentno;

/**
 * The city without a trailing ", ST" when it repeats the state: some DMD
 * rows store "Burlington, NC" in home_city, which would show "NC, NC"
 */
const cityName = (params: { agent: AgentSelect }) => {
  // Params
  const { home_city: city, home_state: state } = params.agent;
  if (
    city &&
    state &&
    city.trim().toUpperCase().endsWith(`, ${state.trim().toUpperCase()}`)
  ) {
    return city.trim().slice(0, -(state.trim().length + 2));
  }
  return city;
};

/** A lowercase /agent/search row as a SearchAgent */
const toSearchAgent = (params: { agent: AgentSelect }): SearchAgent => {
  // Params
  const { agent } = params;
  return {
    id: agent.agentno,
    name: agentName({ agent }),
    city: cityName({ agent }),
    state: agent.home_state,
    status: agent.status,
    raw: agent,
  };
};

/**
 * Impersonation Search wired to Dashboard: searches the Dashboard API's
 * /agent/search (which scopes results to the user's downline unless they
 * may impersonate anyone) and keeps the picked agent in AppContext, in
 * memory only, as before. Pages read `impersonatedAgent.agentno` from
 * AppContext. Start and stop go through useSessionActions, which drops the
 * previous agent's cached queries.
 *
 * Used in the sidebar's search band and on the admin Debts page.
 *
 * @param placement - sidebar (the band, full width) or inline on a light
 *   page (Debts; the parent sets the width)
 * @param disabled - Turn the control off (no search, pick or stop)
 */
export function SelectAgentImpersonate(props: {
  placement?: 'sidebar' | 'page';
  disabled?: boolean;
}) {
  // Context
  const { state } = React.useContext(AppContext);
  const {
    state: { user },
  } = React.useContext(AuthContext);
  // Hooks
  const identity = useAllianceIdentity();
  const { startImpersonating, stopImpersonating } = useSessionActions();

  const username: string = user?.getUsername() || '';

  const handleSearch = React.useCallback(
    async (params: { query: string }) => {
      const body = { search: params.query };
      const path = '/agent/search';
      try {
        const response: { data: AgentSelect[] } = await API.post(
          'DashboardAPI',
          path,
          { body },
        );
        return (response.data || [])
          .filter((agent) => agent.agentno && agent.agentno !== username)
          .map((agent) => toSearchAgent({ agent }));
      } catch (error) {
        // A 403 means the agent is outside what this user may search (e.g.
        // not in their downline); the search shows it as no results
        if (
          (error as { response?: { status?: number } })?.response?.status !==
          403
        ) {
          captureError({ data: { error, request: { body, path } } });
        }
        throw error;
      }
    },
    [username],
  );

  if (!identity) {
    return null;
  }

  const impersonated = state.impersonatedAgent;
  const active = impersonated
    ? { id: impersonated.agentno, name: agentName({ agent: impersonated }) }
    : null;

  return (
    <ImpersonationSearch
      placement={props.placement === 'page' ? 'header' : 'sidebar'}
      mode="light"
      scope={identity.role}
      staffAgentName={identity.staffFor?.fullName}
      owner={username}
      active={active}
      disabled={props.disabled}
      onSearch={handleSearch}
      onPick={(agent) => startImpersonating(agent.raw as AgentSelect)}
      onStop={stopImpersonating}
      excludeIds={[identity.user.id, identity.staffFor?.id || ''].filter(
        Boolean,
      )}
    />
  );
}
